/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import type { ComponentProps } from 'react';
import {
    Indicator as RadioGroupIndicatorPrimitive,
    Item as RadioGroupItemPrimitive,
    Root as RadioGroupRootPrimitive,
} from '@radix-ui/react-radio-group';

import { cn } from '@/lib/utils';

function RadioGroup({ className, ...props }: ComponentProps<typeof RadioGroupRootPrimitive>) {
    return (
        <RadioGroupRootPrimitive
            data-slot="radio-group"
            className={cn('grid gap-2 data-[disabled]:pointer-events-none', className)}
            {...props}
        />
    );
}

function RadioGroupItem({ className, ...props }: ComponentProps<typeof RadioGroupItemPrimitive>) {
    return (
        <RadioGroupItemPrimitive
            data-slot="radio-group-item"
            className={cn(
                'peer relative flex size-4 shrink-0 items-center justify-center rounded-full border border-border bg-[var(--bg-input-30)] text-foreground shadow-none outline-none transition-[background-color,border-color,box-shadow,opacity] focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-focus aria-invalid:border-destructive focus-visible:aria-invalid:border-destructive focus-visible:aria-invalid:ring-destructive-focus disabled:cursor-not-allowed data-[state=checked]:border-foreground',
                className
            )}
            {...props}>
            <RadioGroupIndicatorPrimitive
                data-slot="radio-group-indicator"
                className="flex items-center justify-center">
                <span className="size-2 rounded-full bg-current" />
            </RadioGroupIndicatorPrimitive>
        </RadioGroupItemPrimitive>
    );
}

export { RadioGroup, RadioGroupItem };
